import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import axios from 'axios'
import { API_URL, AppConstant, QueueStatusEnum } from '../../../../AppConstant'
import { APIStatus } from '../../../types'
import { callInNextVisitAsync } from '../lobby'

export interface CurrentPatient {
  currentVisit: CurrentVisit
  patient: Patient
  upcomingVisits: UpcomingVisit[] | null
  userPaymentPlan: UserPaymentPlan
  questionResponses: QuestionResponse[] | null
  metrics: Metrics | null
  feedback: Feedback | null
  treatmentTracker: TreatmentTracker | null
  scanData: ScanData | null
  spinalSemgScan: SpinalSemgScan[] | null
  notes: Notes | null
  totalReferredPatients: number | null
  initialWaiver: boolean | null
  lead: Lead | null
  /** Oldest completed payment in cents (the New Patient Special). Null when the patient never paid. */
  firstPaymentAmount: number | null
  isPaymentPastDue: boolean | null
  /** True while the member's pause is in flight; membershipStatus reads in-active then. */
  isPaused: boolean | null
}

/* -----------------------------------------
   currentVisit
----------------------------------------- */
export interface CurrentVisit {
  id: string
  appointmentTime: string
  timezone: string
  status: string
  checkinTime: string | null
  serviceStartTime: string | null
  serviceEndTime: string | null
  zoomLink: string | null
  isFirstBooking: boolean
  needsFullSlot: boolean
  clinicId: string
  chiropractorId: string | null
  bedId: string | null
  patientId: string
  isScanBooking: boolean
  isVirtual: boolean
  screenerId: string | null
  k1Summary: any | null
  isScreening: boolean
  createdAt: string
  updatedAt: string
  patient: Patient
}

/* -----------------------------------------
   patient + nested user
----------------------------------------- */
export interface Patient {
  id: string
  stripeCustomerId: string
  screeningStatus: string
  treatmentType: string | null
  isQuestionareCompleted: boolean
  screenerFeedback: string | null
  screeningSessionRecording: string | null
  notes: string | null
  createdAt: string
  updatedAt: string
  user: PatientUser
}

export interface PatientUser {
  id: string
  firstname: string
  signupThrough: string
  referralCode: string | null
  lastname: string
  phone: string
  unverifiedPhone: string | null
  dob: string
  email: string | null
  unverifiedEmail: string | null
  externalUserId: string | null
  emailVerificationId: string | null
  welcomeEmailSend: boolean
  textConsent: boolean
  profilePic: string | null
  screeningStatus: string
  gender: string | null
  isDeleted: boolean
  createdAt: string
  updatedAt: string
}

/* -----------------------------------------
   upcomingVisits (empty array now but typing kept)
----------------------------------------- */
export interface UpcomingVisit {
  id: string
  appointmentTime: string
  timezone: string
  status: string
  checkinTime: string | null
  serviceStartTime: string | null
  serviceEndTime: string | null
  zoomLink: string | null
  isFirstBooking: boolean
  needsFullSlot: boolean
  clinicId: string
  chiropractorId: string | null
  bedId: string | null
  patientId: string
  isScanBooking: boolean
  isVirtual: boolean
  screenerId: string | null
  k1Summary: string | null
  isScreening: boolean
  createdAt: string
  updatedAt: string
  clinic: Clinic // ✅ added
}

export interface Clinic {
  id: string
  name: string
  code: string
  slug: string
  market: string | null
  coordinates: string | null
  address: string
  country: string | null
  state: string | null
  studioEmail: string | null
  zipCode: string
  reviewLink: string | null
  mapLocation: string | null
  mapImage: string | null
  priority: number
  createdAt: string
  updatedAt: string
}
/* -----------------------------------------
   userPaymentPlan
----------------------------------------- */
export interface UserPaymentPlan {
  patientId: string
  paymentPlanId: string
  remainingVisitCount: number
  subscriptionExpiresAt: string
  remainingSubscriptionCount: number
  maximumSubscriptionCount: number
  membershipStatus: string
  churnReason: string | null
  createdAt: string
  updatedAt: string
}

export interface Feedback {
  userId: string
  clinicId: string
  feedback: number
  paymentDone: boolean
  createdAt: string
  updatedAt: string
}

/* -----------------------------------------
   questionResponses
----------------------------------------- */
export interface QuestionResponse {
  id: string
  text: string
  patientId: string
  questionId: string
  question: Question
  responseOptions: ResponseOption[]
}

export interface Question {
  id: string
  order: number
  text: string
  type: string
  iconIdentifier: string
  createdAt: string
  updatedAt: string
}

export interface ResponseOption {
  id: string
  answerId: string
  optionId: string
  option: ResponseOptionDetail
  priority?: number // 0 = not selected, 1-4 = priority order (1 is highest)
}

export interface ResponseOptionDetail {
  id: string
  text: string
  order: number
}

/* -----------------------------------------
   metrics
----------------------------------------- */
export interface Metrics {
  id: string
  rev: number | null
  atd: number
  /** Same rule as `atd`, but only the last 30 rolling days. Null until backfilled. */
  ltd: number | null
  dst: number
  scanScore: number | null
  membershipMonth: number
  membershipWeek: number
  apm: string | null
  createdAt: string
  updatedAt: string
}

/* -----------------------------------------
   treatmentTracker
----------------------------------------- */
export interface TreatmentTracker {
  code: string
  data: TreatmentTrackerData
  message: string | null
}

export interface TreatmentTrackerData {
  type: string
  treatmentStartedAt: string
  subscriptionStartedAt: string
  isNewMember: boolean
  /** False once the member subscribed on or after the free-month cutoff. */
  freeMonthEligible?: boolean
  visitFrequency: 'high' | 'medium' | 'low'
  weeks?: TreatmentWeek[]
  months?: TreatmentMonth[]
}

export interface TreatmentWeek {
  weekNumber: number
  visitsIntheWeek: number
  isScanWeek: boolean
  symptomLevel: string | null
}

export interface TreatmentMonth {
  monthNumber: number
  visitsInTheMonth: number
  isScanMonth: boolean
  symptomLevel: string | null
}
/* -----------------------------------------
   scanData
----------------------------------------- */

export interface ScanData {
  id: string
  userId: string
  scanScore: number
  visitId: string
  createdAt: string
  updatedAt: string
  visit: ScanVisit
}

export interface ScanVisit {
  id: string
  appointmentTime: string
  timezone: string
  status: string
  checkinTime: string | null
  serviceStartTime: string | null
  serviceEndTime: string | null
  zoomLink: string | null
  isFirstBooking: boolean
  needsFullSlot: boolean
  clinicId: string
  chiropractorId: string | null
  bedId: string | null
  patientId: string
  isScanBooking: boolean
  isVirtual: boolean
  screenerId: string | null
  k1Summary: string | null
  isScreening: boolean
  createdAt: string
  updatedAt: string
  clinic: Clinic
  chiropractor: Chiropractor
}

export interface Chiropractor {
  id: string
  firstname: string
  lastname: string
  signupThrough: string
  referralCode: string | null
  phone: string
  unverifiedPhone: string | null
  dob: string // ISO date string
  email: string
  unverifiedEmail: string | null
  externalUserId: string | null
  emailVerificationId: string | null
  welcomeEmailSend: boolean
  textConsent: boolean
  profilePic: string
  screeningStatus: string // could be union if you know allowed values
  gender: string | null
  isDeleted: boolean
  createdAt: string // ISO date string
  updatedAt: string // ISO date string
}

/* -----------------------------------------
   spinalSemgScan[]
----------------------------------------- */
export interface SpinalSemgScan {
  userScanId: string
  spineLevel: string
  leftValue: number
  rightValue: number
  status: string
  createdAt: string
  updatedAt: string
}

/* -----------------------------------------
   notes
----------------------------------------- */
export interface Notes {
  id: string
  visitId: string
  chiropractorId: string
  subjective: string | null
  objective: string | null
  assessment: string | null
  plan: string | null
  status: string
  emailSummary: string | null
  createdAt: string
  updatedAt: string
  notefile: NoteFile[]
  visit: CurrentVisitWithClinic
  chiropractor: Chiropractor
}

export interface CurrentVisitWithClinic extends CurrentVisit {
  clinic: Clinic
}

export interface Lead {
  id: string
  firstname: string
  lastname: string
  phone: string
  email: string | null
  referralCode: string | null
  utmSource: string | null
  originalSource: string | null
  utmMedium: string | null
  utmCampaign: string | null
  utmContent: string | null
  market: string | null
  loc: string | null
  createdAt: string
  updatedAt: string
}

export interface NoteFile {
  id: string
  mimeType: string
  fileName: string
  isVoiceNote: boolean
  url: string
  relativePath: string
  transcript: string | null
  createdAt: string
  updatedAt: string
}

//scans
interface Scan {
  id: string
}

interface FirstScan {
  id: string
  userId: string
  scanScore: number | null
  visitId: string
  createdAt: string
  updatedAt: string
}

interface Scans {
  userScan: Scan[]
  firstScan: FirstScan
}

interface ScanReport {
  userScanId: string
  spineLevel: string
  leftValue: number
  rightValue: number
  status: string
  createdAt: string
  updatedAt: string
}

interface ScanReportDetails {
  userScanDatas: ScanReport[]
  fistSpinalSemgScan: ScanReport[]
  appointmentTime: string
  chiroProfilePic: string | null
  scanScore: number | null
  nextScanDueIn: number
}

interface AllNotesId {
  id: string
}

// noteById
export interface NoteById {
  id: string
  visitId: string
  chiropractorId: string
  subjective: string
  objective: string
  assessment: string
  plan: string
  status: string
  emailSummary: string
  createdAt: string
  updatedAt: string

  visit: {
    id: string
    appointmentTime: string
    timezone: string
    status: string
    checkinTime: string | null
    serviceStartTime: string | null
    serviceEndTime: string | null
    zoomLink: string | null
    isFirstBooking: boolean
    needsFullSlot: boolean
    clinicId: string
    chiropractorId: string
    bedId: string | null
    patientId: string
    isScanBooking: boolean
    isVirtual: boolean
    screenerId: string | null
    k1Summary: string | null
    isScreening: boolean
    createdAt: string
    updatedAt: string

    clinic: {
      id: string
      name: string
      code: string
      slug: string
      market: string
      coordinates: string | null
      address: string
      country: string
      state: string
      studioEmail: string
      zipCode: string
      reviewLink: string
      mapLocation: string
      mapImage: string
      priority: number
      createdAt: string
      updatedAt: string
    }
  }

  chiropractor: {
    id: string
    clinicId: string
    isActive: boolean
    createdAt: string
    updatedAt: string

    user: {
      id: string
      firstname: string
      lastname: string | null
      signupThrough: string | null
      referralCode: string | null
      phone: string | null
      unverifiedPhone: string | null
      dob: string | null
      email: string | null
      unverifiedEmail: string | null
      externalUserId: string | null
      emailVerificationId: string | null
      welcomeEmailSend: boolean
      textConsent: boolean
      profilePic: string | null
      screeningStatus: string | null
      gender: string | null
      isDeleted: boolean
      createdAt: string
      updatedAt: string
    }
  }
}

interface CurrentPatientState {
  currentPatient: CurrentPatient | null
  sessionStatus: QueueStatusEnum | string
  notesUploadUrl: string
  currentPatientStatus: APIStatus
  currentPatientError: string | null
  startChiropracticStatus: APIStatus
  startChiropracticSession: any | null
  stopChiropracticSession: any | null
  stopChiropracticStatus: APIStatus
  startChiropracticError: string | null
  stopChiropracticError: string | null
  elapsedSeconds: number
  scans: Scans[] | null
  scansStatus: APIStatus
  scanById: ScanReportDetails | null
  scanByIdStatus: APIStatus
  notes: any | null
  notesStatus: APIStatus
  postNotes: any | null
  postNotesStatus: APIStatus
  postNotesError: string | null
  undoVisit: any | null
  undoVisitStatus: APIStatus
  undoVisitError: string | null
  priorityAnswers: any | null
  priorityAnswersStatus: APIStatus
  priorityAnswersError: string | null
  getAllNotesId: AllNotesId[]
  getAllNotesIdStatus: APIStatus
  getAllNotesIdError: string | null
  noteById: NoteById | null
  noteByIdStatus: APIStatus
  noteByIdError: string | null
  postTreatmentPlanTypeStatus: APIStatus
  postTreatmentPlanTypeError: string | null
  dayTagLevel: 'H' | 'M' | 'L'
  updateVisitFrequencyStatus: APIStatus
  updateVisitFrequencyError: string | null
  nextAppointmentDay: string | null
  nextAppointmentDayStatus: APIStatus
  nextAppointmentDayError: string | null
  recordingActive: boolean
}

// Initial State
const initialState: CurrentPatientState = {
  currentPatient: null,
  sessionStatus: QueueStatusEnum.WAITING,
  notesUploadUrl: '',
  currentPatientStatus: APIStatus.idle,
  currentPatientError: null,
  startChiropracticStatus: APIStatus.idle,
  startChiropracticSession: null,
  stopChiropracticStatus: APIStatus.idle,
  stopChiropracticSession: null,
  startChiropracticError: null,
  stopChiropracticError: null,
  elapsedSeconds: 0,
  scans: null,
  scansStatus: APIStatus.idle,
  scanById: null,
  scanByIdStatus: APIStatus.idle,
  notes: null,
  notesStatus: APIStatus.idle,
  postNotes: null,
  postNotesStatus: APIStatus.idle,
  postNotesError: null,
  undoVisit: null,
  undoVisitStatus: APIStatus.idle,
  undoVisitError: null,
  priorityAnswers: null,
  priorityAnswersStatus: APIStatus.idle,
  priorityAnswersError: null,
  getAllNotesId: [],
  getAllNotesIdStatus: APIStatus.idle,
  getAllNotesIdError: null,
  noteById: null,
  noteByIdStatus: APIStatus.idle,
  noteByIdError: null,
  postTreatmentPlanTypeStatus: APIStatus.idle,
  postTreatmentPlanTypeError: null,
  dayTagLevel: 'H',
  updateVisitFrequencyStatus: APIStatus.idle,
  updateVisitFrequencyError: null,
  nextAppointmentDay: null,
  nextAppointmentDayStatus: APIStatus.idle,
  nextAppointmentDayError: null,
  recordingActive: false,
}

// Async Thunks

export const currentPatientAsync = createAsyncThunk<
  CurrentPatient, // return type
  string, // argument: visitId
  { rejectValue: string }
>('currentPatient/getCurrentPatientAsync', async (visitId, thunkAPI) => {
  try {
    const token = localStorage.getItem(AppConstant.DOCTOR_TOKEN)

    const response = await axios.get(`${API_URL}/visit/${visitId}/portal`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'X-Platform-Id': process.env.REACT_APP_PLATFORM_ID,
      },
    })

    if (response.data?.code === 'success') {
      return thunkAPI.fulfillWithValue(response.data.data)
    }

    return thunkAPI.rejectWithValue(response.data?.message || 'Failed to fetch patient')
  } catch (error: any) {
    const errMsg = error.response?.data?.message || error.message || 'Request failed'
    return thunkAPI.rejectWithValue(errMsg)
  }
})

export const startChiropracticAsync = createAsyncThunk(
  'currentPatient/startChiropracticAsync',
  async (visitId: string | undefined, thunkAPI) => {
    //${process.env.REACT_APP_API_BASE_URL || '/api'
    const token = localStorage.getItem(AppConstant.DOCTOR_TOKEN)

    // console.log(nextPatient)
    let response = await axios.post(
      `${API_URL}/clinic/start`,
      {
        visitId: visitId,
        contentType: 'audio/wav',
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Platform-Id': process.env.REACT_APP_PLATFORM_ID,
        },
      },
    )

    if (response.data && response.data.code === 'success') {
      return thunkAPI.fulfillWithValue(response.data.data)
    } else {
      return thunkAPI.rejectWithValue(response.data.message)
    }
  },
)

export const stopChiropracticAsync = createAsyncThunk(
  'currentPatient/stopChiropracticAsync',
  async ({ visitId }: { visitId: string | undefined }, thunkAPI) => {
    //${process.env.REACT_APP_API_BASE_URL || '/api'
    const token = localStorage.getItem(AppConstant.DOCTOR_TOKEN)

    // console.log(nextPatient)
    let response = await axios.post(
      `${API_URL}/clinic/stop`,
      {
        visitId: visitId,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Platform-Id': process.env.REACT_APP_PLATFORM_ID,
        },
      },
    )

    if (response.data && response.data.code === 'success') {
      return thunkAPI.fulfillWithValue(response.data.data)
    } else {
      return thunkAPI.rejectWithValue(response.data.message)
    }
  },
)

// scansAsync

export const getAllScanAsync = createAsyncThunk(
  'currentPatient/getAllScanAsync',
  async ({ patientId }: { patientId: string }, thunkAPI) => {
    const token = localStorage.getItem(AppConstant.DOCTOR_TOKEN)

    let response = await axios
      .get(`${API_URL}/patient/${patientId}/scan`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Platform-Id': process.env.REACT_APP_PLATFORM_ID,
        },
      })
      .then((res) => res)
      .catch((err) => err.response)

    if (response.data && response.data.code === 'success') {
      return thunkAPI.fulfillWithValue(response.data.data)
    } else {
      return thunkAPI.rejectWithValue(response.data.message)
    }
  },
)

export const getScanByIdAsync = createAsyncThunk(
  'currentPatient/getScanByIdAsync',
  async ({ patientId, scanId }: { patientId: string; scanId: string }, thunkAPI) => {
    const token = localStorage.getItem(AppConstant.DOCTOR_TOKEN)

    let response = await axios
      .get(`${API_URL}/patient/${patientId}/scan/${scanId}/data`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Platform-Id': process.env.REACT_APP_PLATFORM_ID,
        },
      })
      .then((res) => res)
      .catch((err) => err.response)

    if (response.data && response.data.code === 'success') {
      return thunkAPI.fulfillWithValue(response.data.data)
    } else {
      return thunkAPI.rejectWithValue(response.data.message)
    }
  },
)

//notes

export const getNotesAsync = createAsyncThunk('currentPatient/getNotesAsync', async (patientId: string, thunkAPI) => {
  const token = localStorage.getItem(AppConstant.DOCTOR_TOKEN)

  let response = await axios
    .get(`${API_URL}/visit/patient/${patientId}/notes`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'X-Platform-Id': process.env.REACT_APP_PLATFORM_ID,
      },
    })
    .then((res) => res)
    .catch((err) => err.response)

  if (response.data && response.data.code === 'success') {
    return thunkAPI.fulfillWithValue({
      patientId,
      notes: response.data.data,
    })
  } else {
    return thunkAPI.rejectWithValue(response.data.message)
  }
})

// patient notes

export const postPatientNotesAsync = createAsyncThunk(
  'currentPatient/postPatientNotesAsync',
  async ({ patientId, notes }: { patientId: string; notes: string }, thunkAPI) => {
    const token = localStorage.getItem(AppConstant.DOCTOR_TOKEN)

    let response = await axios
      .put(
        `${API_URL}/patient/${patientId}/notes`,
        {
          notes: notes,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'X-Platform-Id': process.env.REACT_APP_PLATFORM_ID,
          },
        },
      )
      .then((res) => res)
      .catch((err) => err.response)

    if (response.data && response.data.code === 'success') {
      return thunkAPI.fulfillWithValue({
        patientId,
        notes: response.data.data?.notes || notes,
      })
    } else {
      return thunkAPI.rejectWithValue(response.data.message)
    }
  },
)

// patient notes

export const undoVisitAsync = createAsyncThunk(
  'currentPatient/undoVisitAsync',
  async ({ visitId }: { visitId: string }, thunkAPI) => {
    const token = localStorage.getItem(AppConstant.DOCTOR_TOKEN)

    let response = await axios
      .post(
        `${API_URL}/visit/${visitId}/undo`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'X-Platform-Id': process.env.REACT_APP_PLATFORM_ID,
          },
        },
      )
      .then((res) => res)
      .catch((err) => err.response)

    if (response.data && response.data.code === 'success') {
      return thunkAPI.fulfillWithValue({
        visitId: visitId,
        response: response.data,
      })
    } else {
      return thunkAPI.rejectWithValue(response.data.message)
    }
  },
)

// getAllNotesIdAsync

export const getAllNotesIdAsync = createAsyncThunk(
  'currentPatient/getAllNotesIdAsync',
  async ({ patientId }: { patientId: string }, thunkAPI) => {
    const token = localStorage.getItem(AppConstant.DOCTOR_TOKEN)

    let response = await axios
      .get(`${API_URL}/visit/patient/${patientId}/note-id`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Platform-Id': process.env.REACT_APP_PLATFORM_ID,
        },
      })
      .then((res) => res)
      .catch((err) => err.response)

    if (response.data && response.data.code === 'success') {
      return thunkAPI.fulfillWithValue({
        response: response.data.data,
      })
    } else {
      return thunkAPI.rejectWithValue(response.data.message)
    }
  },
)

// getNoteByIdAsync

export const getNoteByIdAsync = createAsyncThunk(
  'currentPatient/getNoteByIdAsync',
  async ({ noteId }: { noteId: string }, thunkAPI) => {
    const token = localStorage.getItem(AppConstant.DOCTOR_TOKEN)

    let response = await axios
      .get(`${API_URL}/patient/notes/${noteId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Platform-Id': process.env.REACT_APP_PLATFORM_ID,
        },
      })
      .then((res) => res)
      .catch((err) => err.response)

    if (response.data && response.data.code === 'success') {
      return thunkAPI.fulfillWithValue({
        response: response.data.data,
      })
    } else {
      return thunkAPI.rejectWithValue(response.data.message)
    }
  },
)

// priorityAnswersAsync

export const priorityAnswersAsync = createAsyncThunk(
  'currentPatient/priorityAnswersAsync',
  async ({ responseOptionId, priority }: { responseOptionId: string; priority: number }, thunkAPI) => {
    const token = localStorage.getItem(AppConstant.DOCTOR_TOKEN)

    let response = await axios
      .post(
        `${API_URL}/questionnaire/answers/priority`,
        { responseOptionId: responseOptionId, priority: priority },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'X-Platform-Id': process.env.REACT_APP_PLATFORM_ID,
          },
        },
      )
      .then((res) => res)
      .catch((err) => err.response)

    if (response.data && response.data.code === 'success') {
      return thunkAPI.fulfillWithValue({
        response: response.data,
      })
    } else {
      return thunkAPI.rejectWithValue(response.data.message)
    }
  },
)

export const postTreatmentPlanType = createAsyncThunk(
  'currentPatient/postTreatmentPlanType',
  async ({ patientId, data, payload }: { patientId: string; data: any; payload: any }, thunkAPI) => {
    const token = localStorage.getItem(AppConstant.DOCTOR_TOKEN)
    let response = await axios.post(`${API_URL}/patient/${patientId}/treatment-tracker`, data, {
      headers: {
        Authorization: `Bearer ${token}`,
        'X-Platform-Id': process.env.REACT_APP_PLATFORM_ID,
      },
    })

    if (response.data && response.data.code === 'success') {
      return thunkAPI.fulfillWithValue(payload)
    } else {
      return thunkAPI.rejectWithValue(response.data.message)
    }
  },
)

export const updateVisitFrequencyAsync = createAsyncThunk<
  { visitFrequency: 'high' | 'medium' | 'low' },
  { patientId: string; visitFrequency: 'high' | 'medium' | 'low'; originalFrequency: 'high' | 'medium' | 'low' },
  { rejectValue: string }
>('currentPatient/updateVisitFrequencyAsync', async ({ patientId, visitFrequency }, thunkAPI) => {
  try {
    const token = localStorage.getItem(AppConstant.DOCTOR_TOKEN)

    const response = await axios.put(
      `${API_URL}/patient/${patientId}/visit-frequency`,
      { visitFrequency },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Platform-Id': process.env.REACT_APP_PLATFORM_ID,
        },
      },
    )

    if (response.data?.code === 'success') {
      return thunkAPI.fulfillWithValue({ visitFrequency })
    }

    return thunkAPI.rejectWithValue(response.data?.message || 'Failed to update visit frequency')
  } catch (error: any) {
    const errMsg = error.response?.data?.message || error.message || 'Request failed'
    return thunkAPI.rejectWithValue(errMsg)
  }
})

export const getNextAppointmentDayAsync = createAsyncThunk<
  { nextAppointmentDay: string },
  string, // visitId
  { rejectValue: string }
>('currentPatient/getNextAppointmentDayAsync', async (visitId, thunkAPI) => {
  try {
    const token = localStorage.getItem(AppConstant.DOCTOR_TOKEN)

    const response = await axios.get(`${API_URL}/visit/${visitId}/next-appointment-day`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'X-Platform-Id': process.env.REACT_APP_PLATFORM_ID,
      },
    })

    if (response.data?.code === 'success') {
      return thunkAPI.fulfillWithValue({ nextAppointmentDay: response.data.data.nextAppointmentDay })
    }

    return thunkAPI.rejectWithValue(response.data?.message || 'Failed to fetch next appointment day')
  } catch (error: any) {
    const errMsg = error.response?.data?.message || error.message || 'Request failed'
    return thunkAPI.rejectWithValue(errMsg)
  }
})

const currentPatientSlice = createSlice({
  name: 'currentPatient',
  initialState,
  reducers: {
    setCurrentPatient: (state, action: PayloadAction<CurrentPatient>) => {
      state.currentPatient = action.payload
    },
    setSessionStatus: (state, action: PayloadAction<QueueStatusEnum>) => {
      state.sessionStatus = action.payload
    },
    setElapsedSeconds: (state, action: PayloadAction<number>) => {
      state.elapsedSeconds = action.payload
    },
    resetElapsedSeconds: (state) => {
      state.elapsedSeconds = 0
    },
    resetCurrentPatient: (state) => {
      state.currentPatient = null
      state.dayTagLevel = 'H'
    },
    resetCurrentPatientStatus: (state) => {
      state.currentPatientStatus = APIStatus.idle
      state.currentPatientError = null
    },
    setScans: (state, action: PayloadAction<Scans[]>) => {
      state.scans = action.payload
    },
    setScansStatus: (state, action: PayloadAction<APIStatus>) => {
      state.scansStatus = action.payload
    },
    setScanById: (state, action: PayloadAction<ScanReportDetails>) => {
      state.scanById = action.payload
    },
    setScanByIdStatus: (state, action: PayloadAction<APIStatus>) => {
      state.scanByIdStatus = action.payload
    },
    setPatientNotes: (state, action: PayloadAction<string>) => {
      if (state.currentPatient?.patient) {
        state.currentPatient.patient.notes = action.payload
      }
    },
    updateResponseOptionPriority: (state, action: PayloadAction<{ responseOptionId: string; priority: number }>) => {
      if (state.currentPatient?.questionResponses) {
        const { responseOptionId, priority } = action.payload
        state.currentPatient.questionResponses.forEach((qr) => {
          qr.responseOptions.forEach((ro) => {
            if (ro.id === responseOptionId) {
              ro.priority = priority
            }
          })
        })
      }
    },
    clearResponseOptionPriority: (state, action: PayloadAction<{ responseOptionId: string }>) => {
      if (state.currentPatient?.questionResponses) {
        const { responseOptionId } = action.payload
        const clearedPriority =
          state.currentPatient.questionResponses
            .flatMap((qr) => qr.responseOptions)
            .find((ro) => ro.id === responseOptionId)?.priority || 0

        state.currentPatient.questionResponses.forEach((qr) => {
          qr.responseOptions.forEach((ro) => {
            if (ro.id === responseOptionId) {
              ro.priority = 0
            } else if (ro.priority !== undefined && ro.priority > clearedPriority && ro.priority > 0) {
              // Decrement priorities that were higher than the cleared one
              ro.priority = Math.max(ro.priority - 1, 0)
            }
          })
        })
      }
    },
    clearNoteById: (state) => {
      state.noteById = null
      state.noteByIdError = null
    },
    clearScans: (state) => {
      state.scans = null
      state.scanById = null
    },
    resetNotesListAndNoteById: (state) => {
      state.getAllNotesId = []
      state.getAllNotesIdStatus = APIStatus.idle
      state.getAllNotesIdError = null
      state.noteById = null
      state.noteByIdStatus = APIStatus.idle
      state.noteByIdError = null
    },
    updateTreatmentTracker: (state, action: PayloadAction<TreatmentTracker>) => {
      if (state.currentPatient) {
        state.currentPatient.treatmentTracker = action.payload
      }
    },
    cycleDayTagLevel: (state) => {
      if (state.dayTagLevel === 'H') {
        state.dayTagLevel = 'M'
      } else if (state.dayTagLevel === 'M') {
        state.dayTagLevel = 'L'
      } else {
        state.dayTagLevel = 'H'
      }
    },
    resetDayTagLevel: (state) => {
      state.dayTagLevel = 'H'
    },
    setRecordingActive: (state, action: PayloadAction<boolean>) => {
      state.recordingActive = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      //currentPatientAsync
      .addCase(currentPatientAsync.pending, (state) => {
        state.currentPatientStatus = APIStatus.pending
        state.currentPatientError = null
      })
      .addCase(currentPatientAsync.fulfilled, (state, action) => {
        state.currentPatientStatus = APIStatus.fulfilled
        state.currentPatient = action.payload
        state.currentPatientError = null
        // Fetch next appointment day when patient data is loaded
        if (action.payload.patient?.id) {
          // Dispatch will be handled by the component/hook
        }
      })
      .addCase(currentPatientAsync.rejected, (state, action) => {
        state.currentPatientStatus = APIStatus.rejected
        state.currentPatientError = action.payload as string
      })

    //startChiropractic
    builder.addCase(startChiropracticAsync.fulfilled, (state, action) => {
      state.startChiropracticStatus = APIStatus.fulfilled
      // state.sessionStatus = action.payload.queueStatus || QueueStatusEnum.SERVICE_IN_PROGRESS
      state.sessionStatus = QueueStatusEnum.SERVICE_IN_PROGRESS
      state.notesUploadUrl = action.payload.notesUploadUrl
      state.startChiropracticSession = action.payload
      state.recordingActive = true
    })
    builder.addCase(startChiropracticAsync.rejected, (state, action) => {
      state.startChiropracticStatus = APIStatus.rejected
    })
    builder.addCase(startChiropracticAsync.pending, (state, action) => {
      state.startChiropracticStatus = APIStatus.pending
    })

    //stopChiropractic
    builder.addCase(stopChiropracticAsync.fulfilled, (state, action) => {
      state.stopChiropracticStatus = APIStatus.fulfilled
      // state.sessionStatus = action.payload.queueStatus || QueueStatusEnum.COMPLETED
      state.sessionStatus = QueueStatusEnum.COMPLETED
      state.stopChiropracticSession = action.payload
      state.recordingActive = false
    })
    builder.addCase(stopChiropracticAsync.rejected, (state, action) => {
      state.stopChiropracticStatus = APIStatus.rejected
    })
    builder.addCase(stopChiropracticAsync.pending, (state, action) => {
      state.stopChiropracticStatus = APIStatus.pending
    })

    //scansAsync
    builder.addCase(getAllScanAsync.fulfilled, (state, action) => {
      state.scansStatus = APIStatus.fulfilled
      state.scans = action.payload
    })
    builder.addCase(getAllScanAsync.rejected, (state, action) => {
      state.scansStatus = APIStatus.rejected
    })
    builder.addCase(getAllScanAsync.pending, (state, action) => {
      state.scansStatus = APIStatus.pending
    })

    //getScanByIdAsync
    builder.addCase(getScanByIdAsync.fulfilled, (state, action) => {
      state.scanByIdStatus = APIStatus.fulfilled
      state.scanById = action.payload
    })
    builder.addCase(getScanByIdAsync.rejected, (state, action) => {
      state.scanByIdStatus = APIStatus.rejected
    })
    builder.addCase(getScanByIdAsync.pending, (state, action) => {
      state.scanByIdStatus = APIStatus.pending
    })

    //postNotes
    builder.addCase(postPatientNotesAsync.fulfilled, (state, action) => {
      state.postNotesStatus = APIStatus.fulfilled
      state.postNotes = action.payload
      // Update currentPatient.patient.notes with the saved value
      if (state.currentPatient?.patient && action.payload.notes !== undefined) {
        state.currentPatient.patient.notes = action.payload.notes
      }
    })
    builder.addCase(postPatientNotesAsync.rejected, (state, action) => {
      state.postNotesStatus = APIStatus.rejected
    })
    builder.addCase(postPatientNotesAsync.pending, (state, action) => {
      state.postNotesStatus = APIStatus.pending
    })

    //getAllNotesIdAsync
    builder.addCase(getAllNotesIdAsync.fulfilled, (state, action) => {
      state.getAllNotesIdStatus = APIStatus.fulfilled
      state.getAllNotesId = Array.isArray(action.payload.response) ? action.payload.response : []
    })
    builder.addCase(getAllNotesIdAsync.rejected, (state, action) => {
      state.getAllNotesIdStatus = APIStatus.rejected
    })
    builder.addCase(getAllNotesIdAsync.pending, (state, action) => {
      state.getAllNotesIdStatus = APIStatus.pending
    })

    //getNoteByIdAsync
    builder.addCase(getNoteByIdAsync.fulfilled, (state, action) => {
      state.noteByIdStatus = APIStatus.fulfilled
      state.noteById = action.payload.response as NoteById
    })
    builder.addCase(getNoteByIdAsync.rejected, (state, action) => {
      state.noteByIdStatus = APIStatus.rejected
      state.noteByIdError = action.payload as string
    })
    builder.addCase(getNoteByIdAsync.pending, (state, action) => {
      state.noteByIdStatus = APIStatus.pending
      state.noteByIdError = null
    })

    //undoVisitAsync
    builder.addCase(undoVisitAsync.fulfilled, (state, action) => {
      state.undoVisitStatus = APIStatus.fulfilled
      state.undoVisit = action.payload
      state.currentPatient = null
      state.undoVisitError = null
      state.sessionStatus = QueueStatusEnum.WAITING
      state.elapsedSeconds = 0
    })
    builder.addCase(undoVisitAsync.rejected, (state, action) => {
      state.undoVisitStatus = APIStatus.rejected
      state.undoVisitError = action.payload as string
    })
    builder.addCase(undoVisitAsync.pending, (state, action) => {
      state.undoVisitStatus = APIStatus.pending
    })

    //priorityAnswersAsync
    builder.addCase(priorityAnswersAsync.fulfilled, (state, action) => {
      state.priorityAnswersStatus = APIStatus.fulfilled
      state.priorityAnswers = action.payload
      // Priority updates are handled optimistically via updateResponseOptionPriority reducer
    })
    builder.addCase(priorityAnswersAsync.rejected, (state, action) => {
      state.priorityAnswersStatus = APIStatus.rejected
    })
    builder.addCase(priorityAnswersAsync.pending, (state, action) => {
      state.priorityAnswersStatus = APIStatus.pending
    })

    //callInNextVisitAsync
    builder.addCase(callInNextVisitAsync.fulfilled, (state) => {
      state.sessionStatus = QueueStatusEnum.WAITING
    })

    //postTreatmentPlanType
    builder.addCase(postTreatmentPlanType.pending, (state) => {
      state.postTreatmentPlanTypeStatus = APIStatus.pending
      state.postTreatmentPlanTypeError = null
    })
    builder.addCase(postTreatmentPlanType.fulfilled, (state, action) => {
      state.postTreatmentPlanTypeStatus = APIStatus.fulfilled
      state.postTreatmentPlanTypeError = null
      // Update currentPatient treatmentTracker with the response
      if (state.currentPatient && action.payload) {
        state.currentPatient.treatmentTracker = action.payload
      }
    })
    builder.addCase(postTreatmentPlanType.rejected, (state, action) => {
      state.postTreatmentPlanTypeStatus = APIStatus.rejected
      state.postTreatmentPlanTypeError = action.payload as string
    })

    //updateVisitFrequencyAsync
    builder.addCase(updateVisitFrequencyAsync.pending, (state, action) => {
      state.updateVisitFrequencyStatus = APIStatus.pending
      state.updateVisitFrequencyError = null
      // Store the previous value for potential revert
      if (state.currentPatient?.treatmentTracker?.data) {
        // The previous value is already stored in the action meta if needed
        // For now, we'll rely on the rejected case to revert
      }
    })
    builder.addCase(updateVisitFrequencyAsync.fulfilled, (state, action) => {
      state.updateVisitFrequencyStatus = APIStatus.fulfilled
      state.updateVisitFrequencyError = null
      // Confirm the optimistic update (value already updated, just confirm it)
      if (state.currentPatient?.treatmentTracker?.data) {
        state.currentPatient.treatmentTracker.data.visitFrequency = action.payload.visitFrequency
      }
    })
    builder.addCase(updateVisitFrequencyAsync.rejected, (state, action) => {
      state.updateVisitFrequencyStatus = APIStatus.rejected
      state.updateVisitFrequencyError = action.payload as string
      // Revert optimistic update on error
      // The previous value is stored in action.meta.arg.originalFrequency
      if (state.currentPatient?.treatmentTracker?.data && action.meta.arg?.originalFrequency) {
        state.currentPatient.treatmentTracker.data.visitFrequency = action.meta.arg.originalFrequency
      }
    })

    //getNextAppointmentDayAsync
    builder.addCase(getNextAppointmentDayAsync.pending, (state) => {
      state.nextAppointmentDayStatus = APIStatus.pending
      state.nextAppointmentDayError = null
    })
    builder.addCase(getNextAppointmentDayAsync.fulfilled, (state, action) => {
      state.nextAppointmentDayStatus = APIStatus.fulfilled
      state.nextAppointmentDayError = null
      state.nextAppointmentDay = action.payload.nextAppointmentDay
    })
    builder.addCase(getNextAppointmentDayAsync.rejected, (state, action) => {
      state.nextAppointmentDayStatus = APIStatus.rejected
      state.nextAppointmentDayError = action.payload as string
    })
  },

  //
})

// Export actions
export const {
  setCurrentPatient,
  resetCurrentPatient,
  resetCurrentPatientStatus,
  setSessionStatus,
  setElapsedSeconds,
  resetElapsedSeconds,
  setScans,
  setScansStatus,
  cycleDayTagLevel,
  resetDayTagLevel,
  setScanById,
  setScanByIdStatus,
  setPatientNotes,
  resetNotesListAndNoteById,
  updateResponseOptionPriority,
  clearResponseOptionPriority,
  clearNoteById,
  clearScans,
  updateTreatmentTracker,
  setRecordingActive,
} = currentPatientSlice.actions

// Export reducer
export default currentPatientSlice.reducer
